import "./BatteryTasks.scss";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { memo, useEffect, useState } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  ListGroup,
  Row,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Batteries } from "../../api/Batteries";
import { Tasks } from "../../api/Tasks";
import AppLoader from "../../components/AppLoader";
import AppSearch from "../../components/AppSearch";
import IconButton from "../../components/IconButton";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { useToasts } from "../../contexts/toasts";
import { Battery } from "../../models/Battery";
import { Task } from "../../models/Task";

type Props = {
  task: Task;
  actions: React.ReactNode;
};

const TaskItem = memo(({ task, actions }: Props) => {
  return (
    <ListGroup.Item className="d-flex align-items-center">
      <div className="me-3">
        <FontAwesomeIcon icon={regular("check-circle")} size="lg" fixedWidth />
      </div>

      <Col>
        <h6 className="mb-0">{task.name}</h6>
      </Col>

      <Col md={3} className="text-end">
        {actions}
      </Col>
    </ListGroup.Item>
  );
});

export default function BatteryTasks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [tasks, setTasks] = useState<Task[] | null>();
  const [battery, setBattery] = useState<Battery | null>();
  const [searchText, setSearchText] = useState<string>("");

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  useEffect(() => {
    initTasks();
    initBattery();
  }, []);

  const initTasks = () => {
    Tasks.getTasks().then(
      ({ data: tasks }) => setTasks(tasks),
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast!(
          "tasks-error",
          message,
          `Tasks Error ${status ? `(${status})` : ""}`,
          "danger"
        );
      }
    );
  };

  const initBattery = () => {
    Batteries.getBattery(id!).then(
      ({ data: battery }) => {
        setBattery(battery);
      },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast!(
          "battery-error",
          message,
          `Battery Error ${status ? `(${status})` : ""}`,
          "danger"
        );
      }
    );
  };

  const swapTasks =
    (source: number, direction: "up" | "down") => () => {
      const newBattery = { ...battery! };
      const target = direction === "down" ? source + 1 : source - 1;
      newBattery.tasks[target] = newBattery.tasks.splice(
        source,
        1,
        newBattery.tasks[target]
      )[0];
      setBattery({
        ...newBattery,
      });
    };


  const removeTask = (task: number) => () => {
    battery!.tasks.splice(task, 1);
    setBattery({
      ...battery!
    });
  };

  const renderBatteryTasks = () => {
    if (battery!.tasks.length === 0)
      return (
        <Alert className="py-2 mb-0">
          <FontAwesomeIcon icon={solid("info-circle")} />
          <span className="ms-2">No tasks available.</span>
        </Alert>
      );
    return (
      <div>
        <div className="p-1">
          {battery!.tasks.length === 0 && (
            <Alert className="py-2 mb-0">
              <FontAwesomeIcon icon={solid("info-circle")} />
              <span className="ms-2">No tasks in this Battery.</span>
            </Alert>
          )}
          {battery!.tasks!.length > 0 && (
            <ListGroup>
              {battery!.tasks.map((task, j) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  actions={
                    <>
                      {j < battery!.tasks.length - 1 && (
                        <IconButton onClick={swapTasks(j, "down")}>
                          <FontAwesomeIcon icon={solid("arrow-down")} />
                        </IconButton>
                      )}
                      {j > 0 && (
                        <IconButton
                          className="ms-2"
                          onClick={swapTasks(j, "up")}
                        >
                          <FontAwesomeIcon icon={solid("arrow-up")} />
                        </IconButton>
                      )}
                      <IconButton
                        className="ms-2 text-danger"
                        onClick={removeTask(j)}
                      >
                        <FontAwesomeIcon icon={solid("times")} />
                      </IconButton>
                    </>
                  }
                />
              ))}
            </ListGroup>
          )}
        </div>
      </div>
    );
  };

  const addTask = (task: Task) => () => {

    //const newTasks = [...(battery!.tasks as Task[])]; decomment?
    const found = battery!.tasks.findIndex(
      (t) => t.id === task.id
    );
    if (found > -1) {
      showToast!(
        "task-warning",
        "Task already present in selected battery.",
        "Existing Task",
        "warning"
      );
      return;
    }

    battery!.tasks.push(task);


    setBattery({
      ...battery!//,
      // tasks: newTasks decomment ?
    });

  };

  const saveBatteryTasks = () => {
    showLoader!();

    const body = { ...battery! };

    body.tasks = body.tasks.map((task: any) => {
      return task.id;
    });

    Batteries.updateBattery(body).then(
      () => {
        hideLoader!();
        showToast!(
          "battery-updated",
          "Battery updated successfully",
          "Battery updated",
          "success"
        );
        navigate("/batteries");
      },
      (err) => {
        hideLoader!();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast!(
          "battery-error",
          message,
          `Battery Error ${status ? `(${status})` : ""}`,
          "danger"
        );
      }
    );
  };

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  };

  const filterTask = () => {
    return tasks!
      .filter((task) => task.name.toLowerCase()
        .includes(searchText.toLowerCase()))
      .filter((task) => !battery!.tasks
        .map((task) => task.name)
        .includes(task.name))
  }

  const renderTasks = () => {
    if (tasks!.length === 0)
      return (
        <Alert className="py-2 mb-0">
          <FontAwesomeIcon icon={solid("info-circle")} />
          <span className="ms-2">{t("tasksList.noTasks")}</span>
        </Alert>
      );

    return (
      <>
        <AppSearch
          className="mb-3"
          value={searchText}
          onChange={handleSearchChange}
        />
        <ListGroup className="task-pick-list">
          {filterTask()
            .map((task) => (
              <TaskItem
                key={task.id}
                task={task}
                actions={
                  <IconButton className="text-success" onClick={addTask(task)}>
                    <FontAwesomeIcon icon={solid("plus")} />
                  </IconButton>
                }
              />
            ))}
        </ListGroup>
      </>
    );
  };

  const renderCardContent = () => {
    return (
      <>
        <Card.Body>
          <Row>
            <Col>
              <div className="d-flex align-items-center mb-2">
                <Col>
                  <h6 className="">Battery Tasks</h6>
                </Col>
              </div>
              {renderBatteryTasks()}
            </Col>
            <Col>
              <h6 className="pt-1 pb-2">Available Tasks</h6>
              {renderTasks()}
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between bg-white">
          <Button size="sm" variant="light" className="border" onClick={back}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="success"
            className="px-3"
            onClick={saveBatteryTasks}
          >
            <FontAwesomeIcon icon={solid("save")} />
            <span className="ms-2">Save</span>
          </Button>
        </Card.Footer>
      </>
    );
  };

  const renderAsyncContent = () => {
    return tasks && battery ? (
      renderCardContent()
    ) : (
      <div className="d-flex py-3 justify-content-center">
        <AppLoader size={150} />
      </div>
    );
  };

  const back = () => {
    return navigate('/batteries')
  };

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/batteries" }}>
          {t("pages.batteries")}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          "{battery?.name}" {t("pages.tasks")}
        </Breadcrumb.Item>
      </Breadcrumb>

      <Card>{renderAsyncContent()}</Card>
    </Container>
  );
}
